import { Dialog } from "@/design-system/Dialog/Dialog";
import { SegmentedControl } from "@/design-system/SegmentedControl/SegmentedControl";
import { Switch } from "@/design-system/Switch/Switch";
import { trackAutomaticUpdates } from "@/events/profileMixpanel";
import { AppDispatch } from "@/redux/store";
import {
  Platform,
  canUseClubImportSelector,
  getUserSelector,
  updateUser,
} from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";

const Row = ({
  label,
  hint,
  children,
}: {
  label: ReactNode;
  hint?: string;
  children: ReactNode;
}) => (
  <div className="flex items-center justify-between gap-4">
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-sm text-neutral-100">{label}</span>
      {hint && (
        <span className="text-xs leading-snug text-foreground-muted">
          {hint}
        </span>
      )}
    </div>
    <div className="flex-none">{children}</div>
  </div>
);

/**
 * The account settings My Club leans on, in one place: the platform prices
 * are shown for, automatic import updates and the news email. Each change
 * saves straight to the account, as on the profile page.
 */
export const SetupDialog = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation(["myClub", "profile", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(getUserSelector);
  const canUseClubImport = useSelector(canUseClubImportSelector);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t("setup.title")}
      showClose
      negLabel=""
      posLabel=""
      width={460}
    >
      <div className="flex flex-col gap-5">
        <Row label={t("setup.platform")} hint={t("setup.platformHint")}>
          <SegmentedControl<Platform>
            label={t("setup.platform")}
            variant="primary"
            track="panel"
            value={user.platform ?? "Playstation"}
            onChange={(platform) => dispatch(updateUser({ platform }))}
            options={[
              { value: "Playstation", label: t("setup.console") },
              { value: "PC", label: t("setup.pc") },
            ]}
          />
        </Row>
        {canUseClubImport && (
          <Row
            label={
              <>
                <span>{t("profile:allowAutomaticImports")} </span>
                {/* Kept on the title's line. */}
                <span className="whitespace-nowrap text-foreground-muted">
                  {`(${t("setup.recommended")})`}
                </span>
              </>
            }
            hint={t("profile:automaticImportsTooltip")}
          >
            <Switch
              isActive={!!user.allowTracking}
              onChange={(allowTracking: boolean) => {
                trackAutomaticUpdates(allowTracking);
                dispatch(updateUser({ allowTracking }));
              }}
            />
          </Row>
        )}
        <Row label={t("setup.stayUpdated")} hint={t("common:emailNewsOptIn")}>
          <Switch
            isActive={!!user.emailNotification}
            onChange={(emailNotification: boolean) =>
              dispatch(updateUser({ emailNotification }))
            }
          />
        </Row>
      </div>
    </Dialog>
  );
};
